import { Player, Agent, Item, CombatEncounter, CombatScenario, CombatRoll } from '../types/game';

/**
 * Combat Resolution Engine
 *
 * Three scenarios:
 *   AMBUSH       — Player sees agent, agent doesn't see player. Player chooses weapon.
 *   CONFRONTATION — Both see each other. Outcome depends on weapon matchup + chance.
 *   AMBUSHED     — Agent sees player, player doesn't see agent. Agent attacks first.
 *
 * Weapon hierarchy:
 *   Sniper Rifle > Rifle > Pistol > Knife (at range)
 *   Knife beats vest (bypasses armor)
 *   Pistol/Rifle blocked by vest (vest exhausted)
 *   Flashbang breaks any engagement (player escapes)
 *
 * Special rules:
 *   - Sniper rifle requires player to be elevated (rooftop) — ambush only
 *   - Combat knife is silent, melee-only — ambush only
 *   - Improvised Strike (card, not item) — chance-based ambush attack
 */

// NOTE: there used to be a position-agnostic getPrimaryWeapon() here that
// would happily return a pistol from a rooftop or a sniper from the
// ground. All callers now route through getPrimaryUsableWeapon() with an
// isElevated flag so the rooftop-vs-ground rules are enforced uniformly
// for both player and agent. Keeping this comment as a signpost so the
// old helper doesn't get re-introduced by accident.

/**
 * Pick the best weapon that is actually usable given positioning.
 * - On a rooftop: ONLY a sniper rifle reaches a ground-level target. Pistols,
 *   knives, and improvised attacks can't bridge the elevation gap. The
 *   shooter has to descend to use anything else.
 * - On the ground: sniper rifles need a rooftop perch — they're filtered out
 *   here and surfaced as "needs rooftop" via canAmbushWith().
 *
 * Applies symmetrically to player AND agent. Callers should pass the
 * relevant entity's isElevated flag.
 */
function getPrimaryUsableWeapon(items: Item[], isElevated: boolean): Item | undefined {
  const weapons = items.filter(i => i.category === 'weapon');
  const usable = weapons.filter(w => {
    const isSniper = w.id.startsWith('sniper');
    return isElevated ? isSniper : !isSniper;
  });
  return usable.find(i => i.id.startsWith('sniper')) ??
    usable.find(i => i.id.startsWith('pistol')) ??
    usable.find(i => i.id.startsWith('combat-knife')) ??
    usable[0];
}

function hasItem(items: Item[], prefix: string): boolean {
  return items.some(i => i.id.startsWith(prefix));
}

const AGENT_BASE_PREP = 0.3; // Agent's base "prep" chance for unarmed combat

/**
 * Roll a single prep-based combat check.
 */
function rollPrep(side: 'player' | 'agent', chance: number): CombatRoll {
  const roll = Math.random();
  return { side, chance, roll, success: roll < chance };
}

/**
 * Competitive rolling — both sides roll until one succeeds and the other fails.
 * Returns the full roll history and the winner.
 */
function competitiveRoll(
  playerChance: number,
  agentChance: number,
  maxRounds: number = 10,
): { winner: 'player' | 'agent'; rolls: CombatRoll[] } {
  const rolls: CombatRoll[] = [];

  // Edge case: if one side has 0% chance they can never win
  if (playerChance <= 0 && agentChance <= 0) {
    // Both helpless — coin flip
    const winner = Math.random() < 0.5 ? 'player' : 'agent' as const;
    rolls.push({ side: winner, chance: 0.5, roll: 0, success: true });
    return { winner, rolls };
  }
  if (playerChance <= 0) {
    // Agent auto-wins on first successful roll
    const agentRoll = rollPrep('agent', agentChance);
    rolls.push(agentRoll);
    return { winner: 'agent', rolls };
  }
  if (agentChance <= 0) {
    // Player auto-wins on first successful roll
    const playerRoll = rollPrep('player', playerChance);
    rolls.push(playerRoll);
    return { winner: 'player', rolls };
  }

  for (let i = 0; i < maxRounds; i++) {
    const playerRoll = rollPrep('player', playerChance);
    const agentRoll = rollPrep('agent', agentChance);
    rolls.push(playerRoll, agentRoll);

    // One succeeds, other fails → winner
    if (playerRoll.success && !agentRoll.success) {
      return { winner: 'player', rolls };
    }
    if (agentRoll.success && !playerRoll.success) {
      return { winner: 'agent', rolls };
    }
    // Both succeed or both fail → roll again
  }

  // After max rounds, coin flip
  const tiebreak = Math.random() < 0.5 ? 'player' : 'agent' as const;
  rolls.push({ side: tiebreak, chance: 0.5, roll: 0, success: true });
  return { winner: tiebreak, rolls };
}

/**
 * Determine the combat scenario from vision cone checks.
 * When agent/player are provided, checks for the `sniped` scenario:
 * agent elevated with sniper + sees player who is NOT elevated with sniper.
 */
export function determineCombatScenario(
  playerSeesAgent: boolean,
  agentSeesPlayer: boolean,
  agent?: Agent | null,
  player?: Player | null,
): CombatScenario | null {
  // Sniped: agent on rooftop with sniper sees the player, player is NOT
  // also elevated with a sniper (which would be a counter-sniper confrontation).
  if (agent && player && agentSeesPlayer && agent.isElevated
      && agent.equipment.some(i => i.id.startsWith('sniper'))) {
    const playerHasCounterSniper = player.isElevated
      && player.items.some(i => i.id.startsWith('sniper'));
    if (!playerHasCounterSniper) return 'sniped';
    // Both elevated with snipers → confrontation (falls through)
  }

  if (playerSeesAgent && !agentSeesPlayer) return 'ambush';
  if (playerSeesAgent && agentSeesPlayer) return 'confrontation';
  if (!playerSeesAgent && agentSeesPlayer) return 'ambushed';
  return null; // no combat
}

/**
 * Check if a weapon can be used for an ambush attack.
 * - Sniper rifle: must be elevated
 * - Combat knife: always valid (melee ambush)
 * - Pistol: always valid
 */
export function canAmbushWith(weapon: Item, player: Player): { valid: boolean; reason?: string } {
  const isSniper = weapon.id.startsWith('sniper');
  if (isSniper && !player.isElevated) {
    return { valid: false, reason: 'Sniper rifle requires a rooftop position.' };
  }
  // Rooftop = sniper-only. Pistol, knife, and any improvised tool can't
  // physically reach a ground-level target from a roof — descend first.
  if (player.isElevated && !isSniper) {
    return { valid: false, reason: 'From a rooftop, only a sniper rifle works. Descend to use this.' };
  }
  return { valid: true };
}

/**
 * Resolve an ambush — player attacks an unsuspecting agent.
 * Any weapon = instant kill. Improvised Strike = chance-based (Prep bonus only, no base).
 *
 * @param strikeBonus — accumulated bonus from playing Improvised Strike outside combat (+10% each)
 */
export function resolveAmbush(
  playerWeapon: Item | null,
  isImprovisedStrike: boolean,
  strikeBonus: number = 0,
  agent?: Agent | null,
  player?: Player | null,
): CombatEncounter {
  const base: CombatEncounter = {
    scenario: 'ambush',
    phase: 'outcome',
    playerWeapon: playerWeapon ?? undefined,
  };

  if (isImprovisedStrike) {
    // Unarmed ambush with Improvised Strike.
    //
    // Player prep base: strikeBonus + 20% flat for actively engaging.
    // Agent defensive prep: base + any prep the agent itself built
    // during its card phase, so agents who played their own
    // Improvised Strike cards get credit. If the agent has a real
    // weapon, they also get a +50% "armed defender" bump — being
    // armed is a huge advantage against a bare-handed ambush.
    //
    // Note: the agent's bulletproof vest is intentionally NOT consulted
    // anywhere in this path. Vests stop bullets; they don't stop an
    // improvised hand-to-hand attack. Irrelevant whether the player
    // has an unusable ranged weapon sitting in inventory — that doesn't
    // change the nature of the strike that's actually being resolved.
    const playerChance = Math.min(0.9, strikeBonus + 0.2);
    const agentPrepBuilt = agent?.prepBonus ?? 0;
    const agentHasWeapon = !!(agent && getPrimaryUsableWeapon(agent.equipment, agent.isElevated));
    const armedDefenderBonus = agentHasWeapon ? 0.5 : 0;
    const agentChance = Math.min(0.9, AGENT_BASE_PREP + agentPrepBuilt + armedDefenderBonus);

    // Keep rolling until one side succeeds and the other fails — no
    // tie-survives via coin-flip.
    const { winner, rolls } = competitiveRoll(playerChance, agentChance);

    const agentWeaponName = agent ? getPrimaryUsableWeapon(agent.equipment, agent.isElevated)?.name : null;

    if (winner === 'player') {
      return {
        ...base,
        outcome: 'player-kills',
        message: 'You caught the agent off guard with a brutal improvised attack. Target neutralized.',
        playerPrep: playerChance,
        agentPrep: agentChance,
        rolls,
      };
    }

    // Agent wins the roll. Design: launching an ambush commits you —
    // there's no safe "well, it didn't work, I got away" outcome. You
    // kill them or you die, UNLESS you're carrying a flashbang, which
    // is the one sanctioned break-contact tool. The flashbang is
    // consumed on use.
    const playerHasFlashbang = !!(player && hasItem(player.items, 'flashbang'));
    if (playerHasFlashbang) {
      return {
        ...base,
        outcome: 'player-escapes',
        flashbangUsed: true,
        message: agentWeaponName
          ? `Your strike missed and the agent drew their ${agentWeaponName} — you popped a flashbang and broke contact.`
          : 'Your strike missed — you popped a flashbang and broke contact.',
        playerPrep: playerChance,
        agentPrep: agentChance,
        rolls,
      };
    }
    return {
      ...base,
      outcome: 'player-dies',
      message: agentWeaponName
        ? `Your strike missed. The agent reacted with their ${agentWeaponName} and cut you down.`
        : 'Your strike missed. The agent overpowered you bare-handed.',
      playerPrep: playerChance,
      agentPrep: agentChance,
      rolls,
    };
  }

  // Any real weapon in ambush = instant kill. Bulletproof vest is NOT
  // consulted here: ambush = unsuspecting target + aimed shot. If you
  // ever want a vest save in ambush, this is the spot to add it.
  const weaponName = playerWeapon?.name ?? 'weapon';
  const isSilent = playerWeapon?.isSilent;
  return {
    ...base,
    outcome: 'player-kills',
    message: isSilent
      ? `Silent elimination with ${weaponName}. No signal generated. Target neutralized.`
      : `Ambush with ${weaponName}. Target neutralized.`,
  };
}

/**
 * Resolve a confrontation — both parties see each other.
 * Outcome depends on weapon matchup.
 * Prep bonus shifts gun-vs-gun odds in player's favor.
 */
export function resolveConfrontation(player: Player, agent: Agent): CombatEncounter {
  const playerWeapon = getPrimaryUsableWeapon(player.items, player.isElevated);
  const agentWeapon = getPrimaryUsableWeapon(agent.equipment, agent.isElevated);
  const hasVest = hasItem(player.items, 'bulletproof-vest');
  const hasFlashbang = hasItem(player.items, 'flashbang');

  const base: CombatEncounter = {
    scenario: 'confrontation',
    phase: 'outcome',
    playerWeapon,
    agentWeapon,
  };

  // Flashbang always lets you escape
  if (hasFlashbang) {
    return {
      ...base,
      outcome: 'player-escapes',
      flashbangUsed: true,
      message: 'You threw a flashbang and broke contact. The agent is disoriented.',
    };
  }

  const playerHasGun = playerWeapon && (playerWeapon.id.startsWith('pistol') || playerWeapon.id.startsWith('sniper'));
  const agentHasGun = agentWeapon && (agentWeapon.id.startsWith('pistol') || agentWeapon.id.startsWith('sniper'));
  const agentHasKnife = agentWeapon && agentWeapon.id.startsWith('combat-knife');

  // Armed player, unarmed agent — player auto-wins unless the agent has a flashbang
  // (escapes) or a bulletproof vest (only saves them if the player has a gun).
  if (playerWeapon && !agentWeapon) {
    const agentHasFlashbang = hasItem(agent.equipment, 'flashbang');
    if (agentHasFlashbang) {
      return {
        ...base,
        outcome: 'player-escapes',
        flashbangUsed: true,
        message: `The agent threw a flashbang and broke contact before you could fire.`,
      };
    }
    const agentHasVest = hasItem(agent.equipment, 'bulletproof-vest');
    if (playerHasGun && agentHasVest) {
      return {
        ...base,
        outcome: 'player-escapes',
        message: `You fired your ${playerWeapon.name} but the agent's vest absorbed it. They slipped away.`,
      };
    }
    return {
      ...base,
      outcome: 'player-kills',
      message: `You drew your ${playerWeapon.name} first. The agent was defenseless. Target neutralized.`,
    };
  }

  // Player has gun, agent has only knife (unarmed handled above) → player wins
  if (playerHasGun && !agentHasGun) {
    return {
      ...base,
      outcome: 'player-kills',
      message: `You drew your ${playerWeapon!.name} first. The agent didn't stand a chance. Target neutralized.`,
    };
  }

  // Both have guns → chance-based (50% base + Prep bonus)
  if (playerHasGun && agentHasGun) {
    const prepBonus = player.prepBonus ?? 0;
    const playerChance = Math.min(0.9, prepBonus); // cap at 90%, no base — must build prep
    const agentDrawChance = AGENT_BASE_PREP + 0.2; // agent gun draw = 50% base
    const playerRoll = rollPrep('player', playerChance);
    const agentRoll = rollPrep('agent', agentDrawChance);

    // Both succeed: whoever rolled lower (more decisive) wins
    // One succeeds: they win
    // Both fail: re-interpret as standoff → agent slight edge
    let playerWins: boolean;
    if (playerRoll.success && !agentRoll.success) {
      playerWins = true;
    } else if (!playerRoll.success && agentRoll.success) {
      playerWins = false;
    } else if (playerRoll.success && agentRoll.success) {
      // Both drew — lower roll = faster draw
      playerWins = playerRoll.roll < agentRoll.roll;
    } else {
      // Both hesitated — coin flip
      playerWins = Math.random() < 0.5;
    }

    const rolls = [playerRoll, agentRoll];

    if (playerWins) {
      return {
        ...base,
        outcome: 'player-kills',
        playerPrep: playerChance,
        agentPrep: agentDrawChance,
        rolls,
        message: prepBonus > 0
          ? `A tense standoff — your preparation paid off. You fired first. Target neutralized.`
          : `A tense standoff — you fired first. Target neutralized.`,
      };
    } else {
      // Agent shoots first — can vest save you?
      if (hasVest && !agentHasKnife) {
        return {
          ...base,
          outcome: 'player-saved',
          vestExhausted: true,
          playerPrep: playerChance,
          agentPrep: agentDrawChance,
          rolls,
          message: `The agent fired first but your bulletproof vest absorbed the shot. You're alive, barely.`,
        };
      }
      return {
        ...base,
        outcome: 'player-dies',
        playerPrep: playerChance,
        agentPrep: agentDrawChance,
        rolls,
        message: `The agent was faster on the draw. You've been neutralized.`,
      };
    }
  }

  // Agent has gun, player has only knife or nothing
  if (agentHasGun && !playerHasGun) {
    if (hasVest) {
      return {
        ...base,
        outcome: 'player-saved',
        vestExhausted: true,
        message: `The agent opened fire but your vest saved you. Get out of here.`,
      };
    }
    return {
      ...base,
      outcome: 'player-dies',
      message: `Outgunned. The agent opened fire and you had no defense.`,
    };
  }

  // Neither has a gun — competitive prep rolling (unarmed/melee)
  // Improvised Strike in hand gives +20% bonus in unarmed encounters
  const hasStrikeInHand = player.hand.some(c => c.name === 'Improvised Strike');
  const strikeHandBonus = hasStrikeInHand ? 0.2 : 0;
  const playerChance = Math.min(0.9, (player.prepBonus ?? 0) + strikeHandBonus);
  // Agent gets +50% bonus when they have a knife and the player is unarmed
  const agentKnifeBonus = agentHasKnife && !playerWeapon ? 0.5 : 0;
  const agentChance = AGENT_BASE_PREP + agentKnifeBonus;
  const { winner, rolls } = competitiveRoll(playerChance, agentChance);

  if (winner === 'player') {
    return {
      ...base,
      outcome: 'player-kills',
      playerPrep: playerChance,
      agentPrep: agentChance,
      rolls,
      message: playerWeapon
        ? `Close quarters — you struck first with your ${playerWeapon.name}. Target neutralized.`
        : hasStrikeInHand
          ? `A brutal hand-to-hand fight. Your Improvised Strike training gave you the edge. Target neutralized.`
          : `A brutal hand-to-hand fight. Your preparation made the difference. Target neutralized.`,
    };
  }
  return {
    ...base,
    outcome: 'player-dies',
    playerPrep: playerChance,
    agentPrep: agentChance,
    rolls,
    message: `Close quarters combat. The agent overpowered you.`,
  };
}

/**
 * Resolve being ambushed — agent attacks first, player must survive with defense.
 */
export function resolveAmbushed(player: Player, agent: Agent): CombatEncounter {
  const agentWeapon = getPrimaryUsableWeapon(agent.equipment, agent.isElevated);
  const hasVest = hasItem(player.items, 'bulletproof-vest');
  const hasFlashbang = hasItem(player.items, 'flashbang');
  // Hard Target passive: once per round, the player auto-escapes any Ambushed
  // scenario as if they had a flashbang — even without one in inventory.
  const canUseHardTarget = player.hardTargetActive && !player.hardTargetUsedThisRound;

  const base: CombatEncounter = {
    scenario: 'ambushed',
    phase: 'outcome',
    agentWeapon,
  };

  const agentHasGun = agentWeapon && (agentWeapon.id.startsWith('pistol') || agentWeapon.id.startsWith('sniper'));
  const agentHasKnife = agentWeapon && agentWeapon.id.startsWith('combat-knife');

  // Flashbang always saves you
  if (hasFlashbang) {
    return {
      ...base,
      outcome: 'player-escapes',
      flashbangUsed: true,
      message: 'Ambushed! But your flashbang went off just in time. You escaped in the chaos.',
    };
  }

  // Hard Target free flashbang (once per round) kicks in before anything else
  // resolves. The player still spends the passive charge regardless of what
  // weapon the agent had — the free escape is the whole point.
  if (canUseHardTarget) {
    return {
      ...base,
      outcome: 'player-escapes',
      hardTargetUsed: true,
      message: 'Ambushed! You slipped the noose at the last second — training kicked in and you were gone before they could take the shot.',
    };
  }

  // Knife bypasses vest — always lethal
  if (agentHasKnife) {
    return {
      ...base,
      outcome: 'player-dies',
      message: 'The agent got the drop on you with a blade. No armor could save you.',
    };
  }

  // Gun — vest can save you
  if (agentHasGun) {
    if (hasVest) {
      return {
        ...base,
        outcome: 'player-saved',
        vestExhausted: true,
        message: `Ambushed! The agent fired but your bulletproof vest absorbed the shot. You're exposed now.`,
      };
    }
    return {
      ...base,
      outcome: 'player-dies',
      message: `The agent had you in their sights. A single shot — you never saw it coming.`,
    };
  }

  // Agent has no weapon — unarmed ambush.
  //
  // If the player is armed, they get an automatic +50% defensive prep
  // bump (weapon advantage) on top of their built prepBonus. Agent
  // uses its own built prepBonus (from playing Improvised Strike on
  // their turn) + the base prep; no more flat-constant-only roll.
  //
  // Player with no items AND no prep AND no weapon → auto-dies.
  const playerPrep = player.prepBonus ?? 0;
  const playerHasWeapon = !!getPrimaryUsableWeapon(player.items, player.isElevated);
  const armedDefenderBonus = playerHasWeapon ? 0.5 : 0;
  const playerChance = Math.min(0.9, playerPrep + armedDefenderBonus);
  const agentChance = Math.min(0.9, AGENT_BASE_PREP + (agent.prepBonus ?? 0));

  if (playerChance <= 0) {
    return {
      ...base,
      outcome: 'player-dies',
      message: `The agent got the drop on you. With no preparation and no weapon, you didn't stand a chance.`,
    };
  }

  // Competitive roll keeps going until one succeeds and the other
  // fails. In roll-based combat the winner kills the loser — "saved"
  // and "escapes" outcomes are only produced by items (vest, flashbang)
  // or the Hard Target passive, never by winning a roll.
  const { winner, rolls } = competitiveRoll(playerChance, agentChance);

  if (winner === 'player') {
    return {
      ...base,
      outcome: 'player-kills',
      playerPrep: playerChance,
      agentPrep: agentChance,
      rolls,
      message: playerHasWeapon
        ? `The agent jumped you, but your weapon turned the tables — you put them down.`
        : `The agent jumped you bare-handed. You fought back and overpowered them — target neutralized.`,
    };
  }
  return {
    ...base,
    outcome: 'player-dies',
    playerPrep: playerChance,
    agentPrep: agentChance,
    rolls,
    message: `The agent jumped you bare-handed. Despite your preparation, they overpowered you.`,
  };
}

/**
 * Resolve a confrontation where the player has explicitly chosen their weapon.
 * Same matchup logic as resolveConfrontation but uses the player's chosen weapon.
 */
export function resolveConfrontationWithChoice(
  chosenWeapon: Item | null,
  isImprovisedStrike: boolean,
  strikeBonus: number,
  player: Player,
  agent: Agent,
): CombatEncounter {
  // Sniper requires rooftop — ignore it if the player isn't elevated.
  if (chosenWeapon && chosenWeapon.id.startsWith('sniper') && !player.isElevated) {
    chosenWeapon = null;
  }
  const agentWeapon = getPrimaryUsableWeapon(agent.equipment, agent.isElevated);
  const hasVest = hasItem(player.items, 'bulletproof-vest');
  const hasFlashbang = hasItem(player.items, 'flashbang');

  const base: CombatEncounter = {
    scenario: 'confrontation',
    phase: 'outcome',
    playerWeapon: chosenWeapon ?? undefined,
    agentWeapon,
  };

  // Flashbang auto-escape — BUG FIX: only short-circuit when the player
  // didn't explicitly choose a weapon or improvised strike. Previously this
  // branch fired whenever hasFlashbang was true, which hijacked explicit
  // pistol/knife/sniper clicks and turned them into "you threw a flashbang"
  // even though the player never asked for that. The flashbang button and
  // the fallback "fight to the death" path both call this function with
  // `chosenWeapon === null && !isImprovisedStrike`, so gating on that
  // preserves the original auto-use behavior for those entry points while
  // respecting weapon choices from the weapon-button path.
  if (hasFlashbang && chosenWeapon === null && !isImprovisedStrike) {
    return {
      ...base,
      outcome: 'player-escapes',
      flashbangUsed: true,
      message: 'You threw a flashbang and broke contact. The agent is disoriented.',
    };
  }

  // Improvised strike in confrontation — reduced chance (agent is aware)
  if (isImprovisedStrike) {
    const chance = Math.min(0.7, strikeBonus * 0.7); // 70% effectiveness in confrontation
    const roll = Math.random();
    if (roll < chance) {
      return {
        ...base,
        outcome: 'player-kills',
        playerPrep: chance,
        rolls: [{ side: 'player', chance, roll, success: true }],
        message: `A desperate improvised attack in the open. (${Math.round(chance * 100)}% chance) It worked. Target neutralized.`,
      };
    } else {
      // Agent retaliates
      const agentHasGun = agentWeapon && (agentWeapon.id.startsWith('pistol') || agentWeapon.id.startsWith('sniper'));
      if (agentHasGun) {
        if (hasVest) {
          return {
            ...base,
            outcome: 'player-saved',
            vestExhausted: true,
            playerPrep: chance,
            rolls: [{ side: 'player', chance, roll, success: false }],
            message: `Your improvised attack missed. (${Math.round(chance * 100)}% chance) The agent fired back — your vest saved you.`,
          };
        }
        return {
          ...base,
          outcome: 'player-dies',
          playerPrep: chance,
          rolls: [{ side: 'player', chance, roll, success: false }],
          message: `Your improvised attack missed. (${Math.round(chance * 100)}% chance) The agent fired back.`,
        };
      }
      return {
        ...base,
        outcome: 'player-escapes',
        playerPrep: chance,
        rolls: [{ side: 'player', chance, roll, success: false }],
        message: `Your improvised attack missed. (${Math.round(chance * 100)}% chance) You managed to break contact.`,
      };
    }
  }

  const playerHasGun = chosenWeapon && (chosenWeapon.id.startsWith('pistol') || chosenWeapon.id.startsWith('sniper'));
  const agentHasGun = agentWeapon && (agentWeapon.id.startsWith('pistol') || agentWeapon.id.startsWith('sniper'));
  const agentHasKnife = agentWeapon && agentWeapon.id.startsWith('combat-knife');

  // Armed player, unarmed agent — player auto-wins unless the agent has a flashbang
  // (escapes) or a bulletproof vest (only saves them if the player chose a gun).
  if (chosenWeapon && !agentWeapon) {
    const agentHasFlashbang = hasItem(agent.equipment, 'flashbang');
    if (agentHasFlashbang) {
      return {
        ...base,
        outcome: 'player-escapes',
        flashbangUsed: true,
        message: `The agent threw a flashbang and broke contact before you could fire.`,
      };
    }
    const agentHasVest = hasItem(agent.equipment, 'bulletproof-vest');
    if (playerHasGun && agentHasVest) {
      return {
        ...base,
        outcome: 'player-escapes',
        message: `You fired your ${chosenWeapon.name} but the agent's vest absorbed it. They slipped away.`,
      };
    }
    return {
      ...base,
      outcome: 'player-kills',
      message: `You drew your ${chosenWeapon.name} first. The agent was defenseless. Target neutralized.`,
    };
  }

  // Player has gun, agent doesn't (but agent has some weapon, e.g. knife)
  if (playerHasGun && !agentHasGun) {
    return {
      ...base,
      outcome: 'player-kills',
      message: `You drew your ${chosenWeapon!.name} first. The agent didn't stand a chance. Target neutralized.`,
    };
  }

  // Both have guns — prep-based draw
  if (playerHasGun && agentHasGun) {
    const prepBonus = player.prepBonus ?? 0;
    const playerChance = Math.min(0.9, prepBonus);
    const agentDrawChance = AGENT_BASE_PREP + 0.2;
    const playerRoll = rollPrep('player', playerChance);
    const agentRoll = rollPrep('agent', agentDrawChance);

    let playerWins: boolean;
    if (playerRoll.success && !agentRoll.success) {
      playerWins = true;
    } else if (!playerRoll.success && agentRoll.success) {
      playerWins = false;
    } else if (playerRoll.success && agentRoll.success) {
      playerWins = playerRoll.roll < agentRoll.roll;
    } else {
      playerWins = Math.random() < 0.5;
    }

    const rolls = [playerRoll, agentRoll];

    if (playerWins) {
      return {
        ...base,
        outcome: 'player-kills',
        playerPrep: playerChance,
        agentPrep: agentDrawChance,
        rolls,
        message: prepBonus > 0
          ? `A tense standoff — your preparation paid off. You fired first. Target neutralized.`
          : `A tense standoff — you fired first. Target neutralized.`,
      };
    } else {
      if (hasVest && !agentHasKnife) {
        return {
          ...base,
          outcome: 'player-saved',
          vestExhausted: true,
          playerPrep: playerChance,
          agentPrep: agentDrawChance,
          rolls,
          message: `The agent fired first but your bulletproof vest absorbed the shot. You're alive, barely.`,
        };
      }
      return {
        ...base,
        outcome: 'player-dies',
        playerPrep: playerChance,
        agentPrep: agentDrawChance,
        rolls,
        message: `The agent was faster on the draw. You've been neutralized.`,
      };
    }
  }

  // Agent has gun, player doesn't (player chose knife or unarmed)
  if (agentHasGun && !playerHasGun) {
    if (hasVest) {
      return {
        ...base,
        outcome: 'player-saved',
        vestExhausted: true,
        message: `The agent opened fire but your vest saved you. Get out of here.`,
      };
    }
    return {
      ...base,
      outcome: 'player-dies',
      message: `Outgunned. The agent opened fire and you had no defense.`,
    };
  }

  // Neither has a gun — melee/unarmed
  const playerChance = Math.min(0.9, player.prepBonus ?? 0);
  const agentChance = AGENT_BASE_PREP;
  const { winner, rolls } = competitiveRoll(playerChance, agentChance);

  if (winner === 'player') {
    return {
      ...base,
      outcome: 'player-kills',
      playerPrep: playerChance,
      agentPrep: agentChance,
      rolls,
      message: chosenWeapon
        ? `Close quarters — you struck first with your ${chosenWeapon.name}. Target neutralized.`
        : `A brutal hand-to-hand fight. Your preparation made the difference. Target neutralized.`,
    };
  }
  return {
    ...base,
    outcome: 'player-dies',
    playerPrep: playerChance,
    agentPrep: agentChance,
    rolls,
    message: `Close quarters combat. The agent overpowered you.`,
  };
}

/**
 * After combat, apply item consumption (vest exhausted, flashbang used).
 */
export function applyCombatItemEffects(
  playerItems: Item[],
  encounter: CombatEncounter,
): Item[] {
  let items = [...playerItems];
  if (encounter.vestExhausted) {
    const vestIdx = items.findIndex(i => i.id.startsWith('bulletproof-vest'));
    if (vestIdx >= 0) items.splice(vestIdx, 1);
  }
  if (encounter.flashbangUsed) {
    const fbIdx = items.findIndex(i => i.id.startsWith('flashbang'));
    if (fbIdx >= 0) items.splice(fbIdx, 1);
  }
  return items;
}

/**
 * After combat, apply all player-state effects (inventory changes + passive
 * charge consumption). Wraps applyCombatItemEffects so callers only have to
 * write one line at each site.
 */
export function applyCombatPlayerEffects(
  player: Player,
  encounter: CombatEncounter,
): Player {
  return {
    ...player,
    items: applyCombatItemEffects(player.items, encounter),
    // Hard Target: charge is consumed for the rest of the round once used.
    hardTargetUsedThisRound: encounter.hardTargetUsed
      ? true
      : player.hardTargetUsedThisRound,
  };
}

// ─── Sniped scenario ─────────────────────────────────

/**
 * Resolve a sniper attack — agent is elevated with sniper rifle, player
 * is on the ground. No weapon response possible (too far for pistol/knife).
 * Flashbang is useless at range. Only saves: Hard Target, vest, or ducking
 * into a nearby building.
 */
export function resolveSniped(
  player: Player,
  agent: Agent,
  nearestBuildingDistM: number,
): CombatEncounter {
  const agentWeapon = agent.equipment.find(e => e.category === 'weapon');
  const hasVest = hasItem(player.items, 'bulletproof-vest');
  const canUseHardTarget = player.hardTargetActive && !player.hardTargetUsedThisRound;

  const base: CombatEncounter = {
    scenario: 'sniped',
    phase: 'outcome',
    agentWeapon,
  };

  // Hard Target passive: once-per-round free escape
  if (canUseHardTarget) {
    return {
      ...base,
      outcome: 'player-escapes',
      hardTargetUsed: true,
      message: 'A shot cracks overhead — but your instincts kicked in. You dove for cover just in time.',
    };
  }

  // Bulletproof vest absorbs the shot
  if (hasVest) {
    return {
      ...base,
      outcome: 'player-saved',
      vestExhausted: true,
      message: 'The sniper round hit you square in the chest. Your vest absorbed the impact — barely. You\'re alive, but exposed.',
    };
  }

  // Building escape: if within 50m of any building, player ducks inside
  if (nearestBuildingDistM < 50) {
    return {
      ...base,
      outcome: 'player-escapes',
      message: `A shot ricocheted off the wall beside you. You dove into the nearest doorway. The sniper lost their angle.`,
    };
  }

  // No saves — lethal
  return {
    ...base,
    outcome: 'player-dies',
    message: 'A single shot from above. You never heard it coming. The sniper had you dead to rights.',
  };
}
